import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListCampaignsResult } from '@whinerp/client';
import { DataTable, EmptyState, ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';
import { AttributionPanel } from './attribution-panel.js';
import { LandingPagesPanel } from './landing-pages-panel.js';
import { EventsPanel } from './events-panel.js';
import { DeliverabilityPanel } from './deliverability-panel.js';
import { SmsPanel } from './sms-panel.js';
import { MqlSettingsPanel } from '../setup/mql-settings-panel.js';
import { NurturePanel } from './nurture-panel.js';
import { ReferralProgrammePanel } from '../records/referrals-panel.js';
import { LifecycleReportPanel } from '../records/lifecycle-panel.js';
import { FrequencyCapPanel } from './frequency-cap-panel.js';
import { InfluencePanel } from './influence-panel.js';
import { SourceRoiPanel } from './source-roi-panel.js';
import { TargetAccountsPanel } from './target-accounts-panel.js';

/**
 * SC-16 Campaigns.
 *
 * The performance columns carry a "not sent" figure beside the "reached" one,
 * and it is not decoration. A campaign row showing 1,100 reached out of an
 * audience of 1,400 says nothing about the other 300; a row that also shows
 * 300 not sent invites the one click that explains them. Every figure comes
 * from count() over campaign_member, so the parts add up to the whole by
 * construction rather than by hope.
 */

type Campaigns = ListCampaignsResult;

function CampaignsListScreen(): JSX.Element {
  const t = useT();
  const assets = useEntitlement('F-084');
  const { session } = useSession();
  const format = useFormat();

  const campaigns = useQuery<Campaigns, ApiError>({
    queryKey: ['campaigns'],
    queryFn: () => api.listCampaigns({}),
  });

  if (campaigns.isPending) return <CampaignsLoading />;
  if (campaigns.isError) {
    return <CampaignsError error={campaigns.error} retry={() => void campaigns.refetch()} />;
  }
  if (campaigns.data.campaigns.length === 0) return <CampaignsEmpty reason="no-records" />;

  return (
    <div data-component="CampaignsList" className="flex w-full flex-col gap-3">
      <header className="flex flex-wrap items-baseline gap-3">
        <SectionLabel>{t('campaign.title')}</SectionLabel>
        <Num className="text-meta text-ink-meta">
          {t('list.status.records', {
            values: { count: formatCount(campaigns.data.campaigns.length, format) },
          })}
        </Num>
        {/*
          * The other view of the same campaigns.
          *
          * This one carries what each campaign DID - members, sent, refused,
          * opened - which is a query per row and does not page. SC-59 is the
          * one that scans hundreds.
          */}
        {assets.enabled ? (
          <Link data-asset-library to="/assets" className="ms-auto text-dense text-bronze-text hover:text-ink">
            {t('assets.library')}
          </Link>
        ) : null}
        <Link
          data-campaigns-list
          to="/campaigns/list"
          className={assets.enabled ? 'text-dense text-bronze-text hover:text-ink' : 'ms-auto text-dense text-bronze-text hover:text-ink'}
        >
          {t('list.campaigns.showAsList')}
        </Link>
      </header>

      <Panel>
        <DataTable
          mode={session.preferences.density}
          data-campaigns-table
          label={t('campaign.title')}
          rows={campaigns.data.campaigns}
          rowId={(row) => row.campaignId}
          rowHref={(row) => `/campaigns/${row.campaignId}`}
          columns={[
            { key: 'name', header: t('campaign.name'), essential: true, cell: (row) => row.name },
            { key: 'channel', header: t('campaign.channel'), cell: (row) => row.channel },
            { key: 'status', header: t('campaign.status'), cell: (row) => row.status },
            {
              key: 'members',
              header: t('campaign.members'),
              align: 'end',
              cell: (row) => formatCount(row.performance.members, format),
            },
            {
              // Reached and refused are kept in Simple together: F-062 is that a
              // campaign shows what it reached AND what it did not, and one of
              // those numbers without the other is the half that flatters.
              essential: true,
              key: 'sent',
              header: t('campaign.sent'),
              align: 'end',
              cell: (row) => formatCount(row.performance.sent, format),
            },
            {
              essential: true,
              key: 'refused',
              header: t('campaign.refused'),
              align: 'end',
              /* The shortfall, on the row, always. A reached figure alone
                 says nothing about who was left out. */
              cell: (row) => formatCount(row.performance.refused, format),
            },
            {
              key: 'sentAt',
              header: t('campaign.sentAt'),
              cell: (row) => (row.sentAt ? formatDate(row.sentAt, format) : ''),
            },
          ]}
          labels={{
            sortBy: (column) => t('list.sortBy', { values: { column } }),
            selectRow: (row) => t('list.selectRow', { values: { row } }),
            rowsShown: (count) => t('list.rowsShown', { values: { count: formatCount(count, format) } }),
          }}
        />
      </Panel>

      {/* Which campaigns the won revenue came through, two models at once (F-080). */}
      {/* Hosted pages and which version of each is live (F-072). */}
      <LandingPagesPanel />
      <EventsPanel />

      <AttributionPanel />

      {/* The MQL threshold and what sales said about it, where marketing works (F-078). */}
      <MqlSettingsPanel />
      <NurturePanel />
      <FrequencyCapPanel />
      <ReferralProgrammePanel />
      <LifecycleReportPanel />
      <InfluencePanel />
      <SourceRoiPanel />
      <TargetAccountsPanel />
      <DeliverabilityPanel />
      <SmsPanel />
    </div>
  );
}

function CampaignsEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="Megaphone"
      title={t('campaign.empty.title')}
      body={t('campaign.empty.body')}
      primary={{ label: t('empty.action.newCampaign'), icon: 'Plus', href: '/campaigns' }}
    />
  );
}

function CampaignsLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-2" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {Array.from({ length: 6 }, (_, row) => (
        <Skeleton key={row} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}

function CampaignsError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Campaigns could not be loaded"
      body={error instanceof Error ? error.message : 'The campaign list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-16',
  route: '/campaigns',
  surface: 'page',
  title: 'Campaigns',
  features: ['F-062', 'F-080', 'F-078'],
  entitlement: 'F-062',
  reachedBy: [{ kind: 'nav', item: 'campaigns' }],
  components: ['DataTable'],
  elements: ['CampaignsList'],
  states: { empty: CampaignsEmpty, loading: CampaignsLoading, error: CampaignsError },
  component: CampaignsListScreen,
});

export default screen;
